import React, { useCallback, useMemo, useRef, useState } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { Box, CircularProgress } from "@mui/material";
import { MapLocation } from "../../models/models";
import CityMarker from "./CityMarker";
import RfsMarker, { CompanyGroup } from "./RfsMarker";
import MapLegend from "./MapLegend";
import HomeMarker from "./HomeMarker";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { dt } from "../../../../shared/themes/redesignTokens";
import { GOOGLE_MAPS_LIBRARIES, GOOGLE_MAPS_SCRIPT_ID } from "../../../../shared/config/googleMapsConfig";

// ===================================================================
//  DESIGN TOKENS
// ===================================================================


// ===================================================================
//  MAP CONFIG
// ===================================================================

const DEFAULT_CENTER = { lat: 49.8, lng: 15.5 };
const DEFAULT_ZOOM = 7;
export const ZOOM_THRESHOLD = 12;

/** Pixel distance threshold for clustering nearby city markers */
const CLUSTER_PIXEL_DISTANCE = 70;

const containerStyle: React.CSSProperties = {
  width: "100%",
  height: "100%",
};

// Clean map style: reduce POI noise
const mapStyles = [
  {
    featureType: "poi",
    elementType: "labels",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "poi.business",
    stylers: [{ visibility: "off" }],
  },
  {
    featureType: "transit",
    elementType: "labels.icon",
    stylers: [{ visibility: "off" }],
  },
];

const mapOptions = {
  styles: mapStyles,
  disableDefaultUI: false,
  zoomControl: true,
  mapTypeControl: false,
  streetViewControl: false,
  fullscreenControl: false,
  gestureHandling: "greedy" as const,
};

// ===================================================================
//  PROXIMITY CLUSTERING LOGIC
// ===================================================================

/**
 * Estimate pixel distance between two lat/lng points at a given zoom level.
 * Uses the Mercator projection approximation.
 */
const latLngToPixel = (
  lat: number,
  lng: number,
  zoom: number
): { x: number; y: number } => {
  const scale = Math.pow(2, zoom) * 256;
  const x = ((lng + 180) / 360) * scale;
  const sinLat = Math.sin((lat * Math.PI) / 180);
  const y = (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale;
  return { x, y };
};

const pixelDistance = (
  p1: { x: number; y: number },
  p2: { x: number; y: number }
): number => {
  const dx = p1.x - p2.x;
  const dy = p1.y - p2.y;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Cluster nearby MapLocations based on pixel proximity at the current zoom level.
 * Returns merged MapLocation objects where nearby locations are combined.
 */
const clusterLocations = (
  locations: MapLocation[],
  zoom: number
): MapLocation[] => {
  if (locations.length <= 1) return locations;

  // Convert each location to pixel coords
  const pixels = locations.map((loc) => latLngToPixel(loc.latitude, loc.longitude, zoom));

  // Track which locations have been assigned to a cluster
  const assigned = new Array(locations.length).fill(false);
  const clusters: MapLocation[] = [];

  for (let i = 0; i < locations.length; i++) {
    if (assigned[i]) continue;

    // Start a new cluster with location i
    const clusterIndices = [i];
    assigned[i] = true;

    // Find all unassigned locations within pixel threshold of ANY member of this cluster
    let changed = true;
    while (changed) {
      changed = false;
      for (let j = 0; j < locations.length; j++) {
        if (assigned[j]) continue;
        // Check distance to any member of the current cluster
        for (const ci of clusterIndices) {
          if (pixelDistance(pixels[ci], pixels[j]) < CLUSTER_PIXEL_DISTANCE) {
            clusterIndices.push(j);
            assigned[j] = true;
            changed = true;
            break;
          }
        }
      }
    }

    // Merge all locations in this cluster into one virtual MapLocation
    if (clusterIndices.length === 1) {
      clusters.push(locations[i]);
    } else {
      const locs = clusterIndices.map((idx) => locations[idx]);
      const allRequests = locs.flatMap((loc) => loc.requests);
      const totalCount = locs.reduce((sum, loc) => sum + loc.totalCount, 0);
      const urgentCount = locs.reduce((sum, loc) => sum + loc.urgentCount, 0);
      const highCount = locs.reduce((sum, loc) => sum + loc.highCount, 0);
      const lowCount = locs.reduce((sum, loc) => sum + loc.lowCount, 0);

      // Use the centroid of all cluster members
      const avgLat = locs.reduce((sum, loc) => sum + loc.latitude, 0) / locs.length;
      const avgLng = locs.reduce((sum, loc) => sum + loc.longitude, 0) / locs.length;

      // Build a combined city label (e.g., "Bratislava" if all same city, else "Bratislava +2")
      const uniqueCities = Array.from(new Set(locs.map((loc) => loc.city)));
      const clusterCity =
        uniqueCities.length === 1
          ? uniqueCities[0]
          : `${uniqueCities[0]} +${uniqueCities.length - 1}`;

      // Use the first location's country info (they should be close together)
      const mergedLocation: MapLocation = {
        city: clusterCity,
        countryName: locs[0].countryName,
        countryId: locs[0].countryId,
        latitude: avgLat,
        longitude: avgLng,
        totalCount,
        urgentCount,
        highCount,
        lowCount,
        requests: allRequests,
      };

      // Store original cities for dimmed lookup
      (mergedLocation as any)._originalCities = locs.map((loc) => loc.city);

      clusters.push(mergedLocation);
    }
  }

  return clusters;
};

// ===================================================================
//  COMPONENT
// ===================================================================

interface MapContainerProps {
  locations: MapLocation[];
  companyGroups: CompanyGroup[];
  selectedCity: string | null;
  selectedCompanyId: string | null;
  onMarkerClick: (location: MapLocation) => void;
  onCompanyMarkerClick: (companyGroup: CompanyGroup) => void;
  onZoomChanged: (zoom: number) => void;
  loading?: boolean;
  providerLocation?: { lat: number; lng: number } | null;
}

const MapContainerComponent: React.FC<MapContainerProps> = ({
  locations,
  companyGroups,
  selectedCity,
  selectedCompanyId,
  onMarkerClick,
  onCompanyMarkerClick,
  onZoomChanged,
  loading,
  providerLocation,
}) => {
  const { translate } = useTranslation(["serviceBoard"]);
  const mapRef = useRef<any>(null);
  const [zoomLevel, setZoomLevel] = useState(DEFAULT_ZOOM);

  const apiKey = (window as any).app?.config?.googleMapsApiKey || "";

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: apiKey,
    id: GOOGLE_MAPS_SCRIPT_ID,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const onLoad = useCallback((map: any) => {
    mapRef.current = map;
  }, []);

  const onUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  const handleZoomChanged = useCallback(() => {
    if (mapRef.current) {
      const newZoom = mapRef.current.getZoom();
      if (typeof newZoom === "number") {
        setZoomLevel(newZoom);
        onZoomChanged(newZoom);
      }
    }
  }, [onZoomChanged]);

  const showIndividualMarkers = zoomLevel >= ZOOM_THRESHOLD;

  // Cluster nearby locations based on current zoom level (only for zoomed-out mode)
  const clusteredLocations = useMemo(() => {
    if (showIndividualMarkers) return locations;
    return clusterLocations(locations, zoomLevel);
  }, [locations, zoomLevel, showIndividualMarkers]);

  if (loadError) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          fontFamily: dt.font,
          color: dt.textSecondary,
          fontSize: "14px",
        }}
      >
        Failed to load Google Maps
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          gap: 2,
        }}
      >
        <CircularProgress size={32} sx={{ color: dt.blue }} />
        <Box
          sx={{
            fontFamily: dt.font,
            fontSize: "13px",
            color: dt.textSecondary,
          }}
        >
          {translate("mapLoading")}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        options={mapOptions}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onZoomChanged={handleZoomChanged}
      >
        {/* City cluster markers (below zoom threshold) — with proximity clustering */}
        {!showIndividualMarkers &&
          clusteredLocations.map((location, idx) => (
            <CityMarker
              key={`cluster-${location.city}-${location.latitude.toFixed(4)}-${location.longitude.toFixed(4)}-${idx}`}
              location={location}
              isSelected={selectedCity === location.city}
              onClick={onMarkerClick}
            />
          ))}

        {/* Company group markers (at/above zoom threshold) */}
        {showIndividualMarkers &&
          companyGroups.map((cg) => (
            <RfsMarker
              key={`company-${cg.companyId}-${cg.lat.toFixed(4)}-${cg.lng.toFixed(4)}`}
              companyGroup={cg}
              isSelected={selectedCompanyId === cg.companyId}
              onClick={onCompanyMarkerClick}
            />
          ))}

        {/* Home marker for logged-in user's company */}
        {providerLocation && (
          <HomeMarker position={providerLocation} />
        )}
      </GoogleMap>

      <MapLegend />

      {/* Loading overlay while fetching data */}
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255,255,255,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
          }}
        >
          <CircularProgress size={36} sx={{ color: dt.blue }} />
        </Box>
      )}
    </Box>
  );
};

export default React.memo(MapContainerComponent);
